'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { useRouter } from 'next/navigation';
import { auth } from '@/lib/firebase/config';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import * as Sentry from '@sentry/nextjs';

// Ensure the Firebase session persists across browser restarts
setPersistence(auth, browserLocalPersistence).catch(() => {});

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  /** Sourced from the Admin table via GET /auth/me. Gates super-admin-only UI. */
  isSuperAdmin: boolean;
  /** Own effective permissions from GET /auth/me; use `can()` from lib/permissions. */
  permissions: string[];
  permissionRevokes: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const setAdminSessionCookie = () => {
  document.cookie = 'admin_session=1; path=/; SameSite=Strict; max-age=2592000';
};

const clearAdminSessionCookie = () => {
  document.cookie = 'admin_session=; path=/; max-age=0';
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);

  // Attaches the signed-in user to every Sentry event. Without this GlitchTip stored only
  // a Cloudflare edge IP, so "how many people hit this error" was unanswerable: 455
  // hydration events looked like 18 users when that was really 18 edge nodes.
  // Id only, deliberately — it answers the count question without widening PII.
  useEffect(() => {
    Sentry.setUser(user ? { id: user.uid } : null);
  }, [user?.uid]);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          const res = await fetch(`/api/proxy/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (res.ok) {
            const data = await res.json();
            if (data.role !== 'ADMIN') {
              // Explicitly rejected — sign out
              await signOut(auth);
              clearAdminSessionCookie();
              setUser(null);
              setIsLoading(false);
              router.replace('/login');
              return;
            }
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: data.name || firebaseUser.email,
              role: data.role,
              isSuperAdmin: data.isSuperAdmin ?? false,
              permissions: data.permissions ?? [],
              permissionRevokes: data.permissionRevokes ?? [],
            });
            setAdminSessionCookie();
          } else if (res.status === 401 || res.status === 403 || res.status === 404) {
            // Explicitly unauthorized, forbidden, or user not found — sign out
            await signOut(auth);
            clearAdminSessionCookie();
            setUser(null);
            router.replace('/login');
          }
          // Any other failure (5xx, network, API restarting) — don't sign out.
          // The Firebase session stays intact; next page load will retry.
        } catch {
          // Network/fetch error — API may be temporarily unavailable.
          // Do NOT sign the user out; their Firebase token is still valid.
          // Keep whatever user state we had so the session survives API restarts.
        }
      } else {
        setUser(null);
        clearAdminSessionCookie();
      }
      setIsLoading(false);
    });

    return unsubscribe;
  }, [router]);

  const login = async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const token = await cred.user.getIdToken();

    // Sync user to DB — also handles seeded admin by matching email
    const syncRes = await fetch(`/api/proxy/auth/sync`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: cred.user.email,
        name: cred.user.displayName,
        authProvider: 'EMAIL',
      }),
    });

    if (!syncRes.ok) {
      await signOut(auth);
      throw new Error('Gagal sinkronisasi akun');
    }

    const data = await syncRes.json();
    if (data.role !== 'ADMIN') {
      await signOut(auth);
      throw new Error('Akses ditolak. Akun ini bukan admin.');
    }

    // Prevent redirect loop: middleware reads this cookie before client auth state settles.
    setAdminSessionCookie();
    router.replace('/');
  };

  const logout = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        await fetch(`/api/proxy/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Ignore errors, proceed with logout
    }
    await signOut(auth);
    clearAdminSessionCookie();
    router.replace('/login');
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
